import { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import {
  GOAL_AGGREGATION_KINDS,
  GOAL_ATTRIBUTIONS,
  GOAL_ATTRIBUTION_LABEL,
  GOAL_DATA_SOURCES,
  GOAL_DATA_SOURCE_LABEL,
  GOAL_FREQUENCIES,
  GOAL_FREQUENCY_LABEL,
  type GoalAggregationKind,
  type GoalAttribution,
  type GoalDataSource,
  type GoalFrequency,
  type PartnerGoal,
} from "@colab/shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useUpdateGoal } from "./use-update-goal";

// Mirrors apps/api/src/services/goals-aggregation.ts DATE_PROPS / SUM_PROPS so
// the form only offers properties the engine understands.
const DATE_PROPS_BY_SOURCE: Record<GoalDataSource, string[]> = {
  deal: ["created_at", "updated_at"],
  submission: ["created_at", "decided_at"],
  account: ["created_at", "updated_at", "last_synced_at"],
  journey_progress: ["completed_at", "created_at", "updated_at", "due_at"],
};

const SUM_PROPS_BY_SOURCE: Record<GoalDataSource, string[]> = {
  deal: ["estimated_value_eur"],
  submission: [],
  account: ["annual_revenue", "employee_count"],
  journey_progress: [],
};

const DATE_PROP_LABEL: Record<string, string> = {
  created_at: "Created at",
  updated_at: "Updated at",
  decided_at: "Decided at",
  last_synced_at: "Last synced at",
  completed_at: "Completed at",
  due_at: "Due at",
};

const WORKSPACE = "__workspace";
const NO_PROP = "__none";

export function GoalDefinitionPane({
  goal,
  partners,
  canManage,
  onSpecChange,
}: {
  goal: PartnerGoal;
  partners: Array<{ id: string; company_name: string }>;
  canManage: boolean;
  onSpecChange: () => void;
}) {
  const mutation = useUpdateGoal();
  const [title, setTitle] = useState(goal.title);
  const [description, setDescription] = useState(goal.description ?? "");
  const [partnerId, setPartnerId] = useState<string | null>(goal.partner_id);
  const [dataSource, setDataSource] = useState<GoalDataSource>(goal.data_source);
  const [dateProperty, setDateProperty] = useState<string>(goal.date_property);
  const [aggregationKind, setAggregationKind] = useState<GoalAggregationKind>(
    goal.aggregation_kind,
  );
  const [aggregationProperty, setAggregationProperty] = useState<string | null>(
    goal.aggregation_property,
  );
  const [attribution, setAttribution] = useState<GoalAttribution>(goal.attribution);
  const [frequency, setFrequency] = useState<GoalFrequency>(goal.frequency);

  // Reset local state when the underlying goal changes (e.g. cache refetch).
  useEffect(() => {
    setTitle(goal.title);
    setDescription(goal.description ?? "");
    setPartnerId(goal.partner_id);
    setDataSource(goal.data_source);
    setDateProperty(goal.date_property);
    setAggregationKind(goal.aggregation_kind);
    setAggregationProperty(goal.aggregation_property);
    setAttribution(goal.attribution);
    setFrequency(goal.frequency);
  }, [goal]);

  // When data_source changes, reset date / sum props that no longer apply.
  useEffect(() => {
    const allowed = DATE_PROPS_BY_SOURCE[dataSource];
    if (!allowed.includes(dateProperty)) {
      setDateProperty(allowed[0] ?? "created_at");
    }
    const sumAllowed = SUM_PROPS_BY_SOURCE[dataSource];
    if (
      aggregationKind === "sum" &&
      aggregationProperty &&
      !sumAllowed.includes(aggregationProperty)
    ) {
      setAggregationProperty(sumAllowed[0] ?? null);
    }
  }, [dataSource, dateProperty, aggregationKind, aggregationProperty]);

  function onSave() {
    // Sub-bucket 19 — partner-scope flips are accepted by the worker
    // (`partnerGoalUpdateSchema` was widened to admit a `partner_id` patch).
    // Re-scoping a goal does NOT migrate existing `partner_goal_targets`
    // rows; those stay attached to the previous partner. We gate the flip
    // behind a confirm so admins know to recreate targets after re-scoping.
    if (partnerId !== goal.partner_id) {
      const oldLabel = goal.partner_id
        ? partners.find((p) => p.id === goal.partner_id)?.company_name ??
          "previous partner"
        : "Workspace";
      const newLabel = partnerId
        ? partners.find((p) => p.id === partnerId)?.company_name ?? "new partner"
        : "Workspace";
      const confirmed = window.confirm(
        `Re-scope goal from "${oldLabel}" to "${newLabel}"? Existing targets will not move — you'll need to recreate them.`,
      );
      if (!confirmed) return;
    }

    mutation.mutate(
      {
        id: goal.id,
        partnerId: goal.partner_id,
        patch: {
          partner_id: partnerId,
          title: title.trim(),
          description: description.trim() || null,
          aggregation_kind: aggregationKind,
          aggregation_property:
            aggregationKind === "sum" ? aggregationProperty : null,
          data_source: dataSource,
          date_property: dateProperty,
          attribution,
          frequency,
        },
      },
      {
        onSuccess: () => {
          toast.success("Saved");
          onSpecChange();
        },
      },
    );
  }

  const dateProps = DATE_PROPS_BY_SOURCE[dataSource];
  const sumProps = SUM_PROPS_BY_SOURCE[dataSource];

  return (
    <div className="space-y-4 rounded-xl border bg-card p-5">
      <div className="space-y-1.5">
        <Label htmlFor="g-title">Title</Label>
        <Input
          id="g-title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={200}
          disabled={!canManage}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="g-description">Description</Label>
        <Textarea
          id="g-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={2}
          maxLength={2000}
          disabled={!canManage}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor="g-scope">Scope</Label>
        <Select
          value={partnerId ?? WORKSPACE}
          onValueChange={(v) => setPartnerId(v === WORKSPACE ? null : v)}
          disabled={!canManage}
        >
          <SelectTrigger id="g-scope">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={WORKSPACE}>Workspace (all partners)</SelectItem>
            {partners.map((p) => (
              <SelectItem key={p.id} value={p.id}>
                {p.company_name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <p className="text-xs text-muted-foreground">
          Workspace goals get per-partner targets in the Targets tab.
          Re-scoping a goal leaves existing targets attached to the previous
          partner — recreate the targets after saving.
        </p>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="g-source">Data source</Label>
          <Select
            value={dataSource}
            onValueChange={(v) => setDataSource(v as GoalDataSource)}
            disabled={!canManage}
          >
            <SelectTrigger id="g-source">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {GOAL_DATA_SOURCES.map((d) => (
                <SelectItem key={d} value={d}>
                  {GOAL_DATA_SOURCE_LABEL[d]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="g-date">Date property</Label>
          <Select
            value={dateProperty}
            onValueChange={(v) => setDateProperty(v)}
            disabled={!canManage}
          >
            <SelectTrigger id="g-date">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {dateProps.map((p) => (
                <SelectItem key={p} value={p}>
                  {DATE_PROP_LABEL[p] ?? p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="g-kind">Aggregation</Label>
          <Select
            value={aggregationKind}
            onValueChange={(v) => {
              setAggregationKind(v as GoalAggregationKind);
              if (v === "count") setAggregationProperty(null);
              else if (v === "sum" && sumProps.length > 0)
                setAggregationProperty(sumProps[0]);
            }}
            disabled={!canManage}
          >
            <SelectTrigger id="g-kind">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {GOAL_AGGREGATION_KINDS.map((k) => (
                <SelectItem
                  key={k}
                  value={k}
                  disabled={k === "sum" && sumProps.length === 0}
                >
                  {k === "count" ? "Count of rows" : "Sum of property"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="g-prop">Property</Label>
          <Select
            value={aggregationProperty ?? NO_PROP}
            onValueChange={(v) =>
              setAggregationProperty(v === NO_PROP ? null : v)
            }
            disabled={!canManage || aggregationKind !== "sum"}
          >
            <SelectTrigger id="g-prop">
              <SelectValue placeholder="—" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={NO_PROP}>—</SelectItem>
              {sumProps.map((p) => (
                <SelectItem key={p} value={p}>
                  {p}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1.5">
          <Label htmlFor="g-attr">Attribution</Label>
          <Select
            value={attribution}
            onValueChange={(v) => setAttribution(v as GoalAttribution)}
            disabled={!canManage}
          >
            <SelectTrigger id="g-attr">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {GOAL_ATTRIBUTIONS.map((a) => (
                <SelectItem key={a} value={a}>
                  {GOAL_ATTRIBUTION_LABEL[a]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-muted-foreground">
            Multi-touch is Phase B — Phase A treats it identically to All.
          </p>
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="g-freq">Frequency</Label>
          <Select
            value={frequency}
            onValueChange={(v) => setFrequency(v as GoalFrequency)}
            disabled={!canManage}
          >
            <SelectTrigger id="g-freq">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {GOAL_FREQUENCIES.map((f) => (
                <SelectItem key={f} value={f}>
                  {GOAL_FREQUENCY_LABEL[f]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-md border border-dashed bg-muted/20 px-3 py-2 text-xs text-muted-foreground">
        Filter editing lands in Session 7 alongside the saved-views builder.
        Until then, the goal computes against the entire data source within
        the chosen period.
      </div>

      {canManage && (
        <div className="flex items-center justify-end gap-2 pt-1">
          <Button type="button" onClick={onSave} disabled={mutation.isPending}>
            {mutation.isPending ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save definition
          </Button>
        </div>
      )}
    </div>
  );
}
